import { faDownload } from '@fortawesome/pro-solid-svg-icons'
import type { IntlShape } from 'react-intl'

import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import {
  trackActionPanelActionClicked,
  trackDownloadManifest,
  type AnalyticsTracker,
} from '../../helpers/listViewAnalytics'
import type { ListViewSurface } from '../../helpers/listViewSurface'
import { PANEL_ACTION_ORDER } from '../panelActionOrder'
import type { PanelActionDefinition } from '../types'

const ns = 'Listings.ListView.actionPanel.downloadManifest'

/**
 * Download the row's manifest as a CSV without leaving the list.
 *
 * Always available: whether the lot has a manifest is not on a search row, so
 * the click finds out and the icon shows the outcome — the file downloads, or
 * a status message says there is none. The provider owns that sequence; this
 * only tracks and gates.
 *
 * This runs for each row and we want it to be fast, so it has no hooks.
 */
export const createDownloadManifestPanelAction = ({
  formatMessage,
  surface,
  tracker,
  isSignedIn,
  openUnAuthModal,
  downloadManifest,
}: {
  formatMessage: IntlShape['formatMessage']
  surface?: ListViewSurface
  tracker: AnalyticsTracker
  isSignedIn: boolean
  openUnAuthModal: (reason: string) => void
  downloadManifest: (listing: CombinedListingSearchResult) => void
}): PanelActionDefinition => ({
  id: 'downloadManifest',
  icon: faDownload,
  label: formatMessage({ id: `${ns}.label` }),
  description: formatMessage({ id: `${ns}.description` }),
  order: PANEL_ACTION_ORDER.downloadManifest,
  onClick: (listing) => {
    // Track, then act — a guest's click is still a click.
    trackDownloadManifest(tracker, { listingId: listing.listingId, surface })
    trackActionPanelActionClicked(tracker, {
      action: 'download_manifest',
      listingId: listing.listingId,
      surface,
    })

    if (!isSignedIn) {
      openUnAuthModal(formatMessage({ id: `${ns}.signInReason` }))
      return
    }

    downloadManifest(listing)
  },
})
